import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mantine/core';
import { useIntl } from 'react-intl';

import { getDocumentTypeLabel } from './document-type-label';
import { documentsSectionMessages } from './documents-section-messages';
import styles from './DocumentsSection.module.css';
import { DetailSection } from '../detail-section/DetailSection';

import type { LtlDocument } from '../../../../types/ltl';

type DocumentsSectionProps = {
  documents?: LtlDocument[];
  onPrintDocument: (document: LtlDocument) => void;
};

/**
 * Right-column Documents card (§6.5). Renders one row per document returned by
 * the backend for the shipment, each with a tertiary Print button that invokes
 * `onPrintDocument(document)`. Renders an empty-state message when there are no
 * documents. Purely callback-driven — no transaction id needed.
 */
export const DocumentsSection = ({ documents, onPrintDocument }: DocumentsSectionProps) => {
  const intl = useIntl();
  const hasDocuments = (documents?.length ?? 0) > 0;

  return (
    <DetailSection testId='documents-section' title={intl.formatMessage(documentsSectionMessages.sectionTitle)}>
      {hasDocuments ? (
        <div className={styles.rows}>
          {documents?.map((doc) => (
            <div className={styles.row} key={doc.key}>
              <span className={styles.label}>{intl.formatMessage(getDocumentTypeLabel(doc.documentType))}</span>
              <Button
                data-testid={`print-document-${doc.key}`}
                leftSection={<FontAwesomeIcon icon={faPrint} />}
                onClick={() => onPrintDocument(doc)}
                size='xs'
                variant='ghost'
              >
                {intl.formatMessage(documentsSectionMessages.print)}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <span className={styles.label}>{intl.formatMessage(documentsSectionMessages.noDocuments)}</span>
      )}
    </DetailSection>
  );
};
